"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Search,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  Eye,
  Folder,
  FolderOpen,
  RefreshCw,
  Download,
  AlertTriangle,
  X,
} from "lucide-react"

// Categorías disponibles. Con más de una se muestra el selector; hoy solo existe una.
const CATEGORIES = [{ id: "DocumentosGenerales", label: "Documentos generales", description: "Archivos corporativos" }]

// El buscador y el orden solo aparecen cuando la lista es lo bastante larga como para necesitarlos
const SEARCH_THRESHOLD = 8

const PREVIEWABLE = ["pdf", "jpg", "jpeg", "png", "gif"]
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif"]

const TYPE_PDF = { icon: FileText, tile: "bg-red-50 text-red-700" }
const TYPE_WORD = { icon: FileText, tile: "bg-sky-50 text-sky-700" }
const TYPE_EXCEL = { icon: FileSpreadsheet, tile: "bg-green-50 text-green-700" }
const TYPE_SLIDES = { icon: FileText, tile: "bg-orange-50 text-orange-700" }
const TYPE_IMAGE = { icon: FileImage, tile: "bg-violet-50 text-violet-700" }
const TYPE_DEFAULT = { icon: File, tile: "bg-neutral-50/60 text-neutral-400" }

const EXTENSION_STYLES = {
  pdf: TYPE_PDF,
  doc: TYPE_WORD,
  docx: TYPE_WORD,
  xls: TYPE_EXCEL,
  xlsx: TYPE_EXCEL,
  csv: TYPE_EXCEL,
  ppt: TYPE_SLIDES,
  pptx: TYPE_SLIDES,
  png: TYPE_IMAGE,
  jpg: TYPE_IMAGE,
  jpeg: TYPE_IMAGE,
  gif: TYPE_IMAGE,
}

const SOFT_SHADOW = "shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)]"
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70"

// El tipo ya se ve en el icono y en la etiqueta: el nombre se muestra sin extensión
const stripExtension = (fileName = "") => (fileName.includes(".") ? fileName.slice(0, fileName.lastIndexOf(".")) : fileName)

const getExtension = (fileName = "") => (fileName.includes(".") ? fileName.split(".").pop().toLowerCase() : "")

const formatFileSize = (bytes) => {
  if (!bytes) return ""
  const units = ["B", "KB", "MB", "GB"]
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, i)
  return `${value.toLocaleString("es-CO", { maximumFractionDigits: i === 0 ? 0 : 1 })} ${units[i]}`
}

const DATE_FORMATTER = new Intl.DateTimeFormat("es-CO", { day: "numeric", month: "short", year: "numeric" })

// "21 sept 2026" (sin los "de" que agrega toLocaleDateString)
const formatDate = (value) => {
  const time = Date.parse(value)
  if (!value || Number.isNaN(time)) return ""
  const parts = DATE_FORMATTER.formatToParts(new Date(time))
  const pick = (type) => parts.find((part) => part.type === type)?.value ?? ""
  return `${pick("day")} ${pick("month")} ${pick("year")}`
}

export default function FileLibraryContent({ user, API_BASE_URL, getHeaders }) {
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("recent")
  const [files, setFiles] = useState([])
  const [fileLoading, setFileLoading] = useState(false)
  const [fileError, setFileError] = useState(null)
  const [selectedFileForPreview, setSelectedFileForPreview] = useState(null)

  // Obtener archivos de Azure Storage
  const loadFilesTab = useCallback(
    async (category) => {
      if (!API_BASE_URL || !user?.token) {
        setFileError("No hay conexión disponible")
        return
      }

      try {
        setFileLoading(true)
        setFileError(null)

        const response = await fetch(`${API_BASE_URL}/azureStorage/${category}`, {
          method: "GET",
          headers: getHeaders(),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))

          if (response.status === 404 || errorData.error?.includes("does not exist")) {
            setFileError("La carpeta aún no está disponible. Los archivos aparecerán aquí cuando se suban.")
            setFiles([])
            return
          }

          if (response.status === 500) {
            setFileError("No se pudo acceder a la carpeta en este momento. Intente de nuevo o contacte al equipo de TI.")
            setFiles([])
            return
          }

          throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (!data.success) {
          setFiles([])
          setFileError(data.message || "No se pudieron cargar los archivos")
          return
        }

        // Una carpeta vacía no es un error: se muestra como estado vacío
        const filesWithMetadata = (data.contenido || [])
          .filter((item) => item.tipo === "file")
          .map((file) => {
            const fileName = file.nombre
            const fileExtension = getExtension(fileName)
            const url = file.uri || `${API_BASE_URL}/azureStorage/${category}/${encodeURIComponent(fileName)}`

            return {
              id: fileName,
              name: fileName,
              type: fileExtension.toUpperCase(),
              extension: fileExtension,
              size: file.tamaño || file.size || 0,
              downloadUrl: url,
              previewUrl: url,
              lastModified: file.fechaModificacion || file.lastModified || null,
              category: category,
              canPreview: PREVIEWABLE.includes(fileExtension),
            }
          })

        setFiles(filesWithMetadata)
      } catch (error) {
        console.error("Error al cargar archivos desde Azure Storage:", error)
        setFileError("No se pudo conectar con el servidor. Intente nuevamente.")
        setFiles([])
      } finally {
        setFileLoading(false)
      }
    },
    [API_BASE_URL, user?.token, getHeaders],
  )

  // Función para descargar archivo
  const downloadFile = async (file) => {
    try {
      if (file.downloadUrl.includes("file.core.windows.net")) {
        const link = document.createElement("a")
        link.href = file.downloadUrl
        link.download = file.name
        link.target = "_blank"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }

      if (user?.token) {
        try {
          const response = await fetch(file.downloadUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          })

          if (response.ok) {
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.download = file.name
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
            return
          }
        } catch (fetchError) {
          console.warn("Error al descargar con autenticación:", fetchError)
        }
      }

      const link = document.createElement("a")
      link.href = file.downloadUrl
      link.download = file.name
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error("Error al descargar archivo:", error)
      alert("Error al descargar el archivo. Intente nuevamente.")
    }
  }

  // Cargar archivos al montar y al cambiar de categoría
  useEffect(() => {
    loadFilesTab(activeCategory)
  }, [loadFilesTab, activeCategory])

  // Cerrar la vista previa con Escape
  useEffect(() => {
    if (!selectedFileForPreview) return
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelectedFileForPreview(null)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [selectedFileForPreview])

  // Filtrar por búsqueda y ordenar (los sin fecha van al final en "recientes")
  const visibleFiles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const list = term ? files.filter((file) => file.name.toLowerCase().includes(term)) : files
    const byName = (a, b) => a.name.localeCompare(b.name, "es", { numeric: true, sensitivity: "base" })
    const byRecent = (a, b) => (Date.parse(b.lastModified) || 0) - (Date.parse(a.lastModified) || 0)
    return [...list].sort(sortBy === "name" ? byName : byRecent)
  }, [files, searchTerm, sortBy])

  const counterText = searchTerm.trim()
    ? `${visibleFiles.length} de ${files.length} ${files.length === 1 ? "archivo" : "archivos"}`
    : `${files.length} ${files.length === 1 ? "archivo" : "archivos"}`

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Encabezado */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-cafe-700 sm:text-4xl">
            Archivos
          </h2>
          <p className="mt-1 text-sm text-neutral-400">Consulte y descargue los documentos corporativos.</p>
        </div>
        <button
          onClick={() => loadFilesTab(activeCategory)}
          disabled={fileLoading}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-cafe-700 transition-colors duration-200 hover:bg-cafe-100/50 disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS}`}
        >
          <RefreshCw className={`h-4 w-4 ${fileLoading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      <section className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6">
        {/* Selector de categoría: solo cuando hay más de una */}
        {CATEGORIES.length > 1 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                aria-pressed={category.id === activeCategory}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${FOCUS} ${
                  category.id === activeCategory
                    ? "bg-[#5D4037] text-white"
                    : "bg-white/70 text-cafe-700 hover:bg-white"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        )}

        {/* Búsqueda y orden: solo con listas largas */}
        {files.length > SEARCH_THRESHOLD && (
          <div className="mb-5 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-300" />
              <input
                type="text"
                placeholder="Buscar archivo"
                aria-label="Buscar archivo"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-cafe-200/60 bg-white py-2.5 pl-10 pr-4 text-sm text-cafe-800 transition-colors duration-200 placeholder:text-neutral-300 hover:border-cafe-300 focus:border-cafe-400 focus:outline-none focus:ring-4 focus:ring-cafe-100/70"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Ordenar archivos"
              className="rounded-xl border border-cafe-200/60 bg-white px-3 py-2.5 text-sm text-cafe-800 transition-colors duration-200 hover:border-cafe-300 focus:border-cafe-400 focus:outline-none focus:ring-4 focus:ring-cafe-100/70"
            >
              <option value="recent">Más recientes</option>
              <option value="name">Nombre (A-Z)</option>
            </select>
          </div>
        )}

        {/* Cargando */}
        {fileLoading && (
          <ul
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            role="status"
            aria-label="Cargando archivos"
          >
            {[0, 1, 2].map((n) => (
              <li key={n} className="flex animate-pulse flex-col gap-3 rounded-xl bg-white/70 p-4">
                <div className="flex items-start gap-3">
                  <span className="h-11 w-11 flex-shrink-0 rounded-xl bg-cafe-100/60" />
                  <span className="flex-1 space-y-2 pt-1">
                    <span className="block h-3 w-4/5 rounded bg-cafe-100/60" />
                    <span className="block h-2.5 w-1/4 rounded bg-cafe-100/40" />
                  </span>
                </div>
                <span className="h-9 rounded-lg bg-cafe-100/40" />
              </li>
            ))}
          </ul>
        )}

        {/* Error */}
        {fileError && !fileLoading && (
          <div className={`rounded-xl bg-white px-6 py-8 text-center ${SOFT_SHADOW}`}>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sol-200/25 text-cafe-600">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h4 className="font-display text-lg font-semibold text-cafe-700">No se pudieron mostrar los archivos</h4>
            <p className="mx-auto mt-1 max-w-md text-sm text-neutral-400">{fileError}</p>
            <button
              onClick={() => loadFilesTab(activeCategory)}
              className={`mt-4 inline-flex items-center gap-2 rounded-lg bg-cafe-100/50 px-4 py-2 text-sm font-medium text-cafe-700 transition-colors hover:bg-cafe-100 ${FOCUS}`}
            >
              <RefreshCw className="h-4 w-4" />
              Reintentar
            </button>
          </div>
        )}

        {/* Archivos */}
        {!fileLoading && !fileError && visibleFiles.length > 0 && (
          <ul className="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleFiles.map((file) => {
              const style = EXTENSION_STYLES[file.extension] || TYPE_DEFAULT
              const Icon = style.icon
              const meta = [file.type, formatFileSize(file.size), formatDate(file.lastModified)].filter(Boolean).join(" · ")

              return (
                <li key={file.id} className={`flex flex-col gap-3 rounded-xl bg-white p-4 ${SOFT_SHADOW}`}>
                  <div className="flex items-start gap-3">
                    <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${style.tile}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <p
                      title={file.name}
                      className="line-clamp-2 min-w-0 flex-1 break-words pt-0.5 text-sm font-semibold leading-snug text-cafe-800"
                    >
                      {stripExtension(file.name)}
                    </p>
                  </div>

                  <span
                    aria-hidden="true"
                    className="h-px w-full bg-gradient-to-r from-transparent via-cafe-200/70 to-transparent"
                  />

                  <div className="mt-auto flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs text-neutral-400">{meta}</span>

                    <div className="flex flex-shrink-0 items-center gap-1">
                      {file.canPreview && (
                        <button
                          onClick={() => setSelectedFileForPreview(file)}
                          title="Vista previa"
                          aria-label={`Vista previa de ${file.name}`}
                          className={`rounded-lg p-2 text-cafe-600 transition-colors hover:bg-cafe-100/50 hover:text-cafe-800 ${FOCUS}`}
                        >
                          <Eye className="h-[18px] w-[18px]" />
                        </button>
                      )}
                      <button
                        onClick={() => downloadFile(file)}
                        title="Descargar"
                        aria-label={`Descargar ${file.name}`}
                        className={`rounded-lg bg-cafe-100/50 p-2 text-cafe-700 transition-colors hover:bg-cafe-100 hover:text-cafe-800 ${FOCUS}`}
                      >
                        <Download className="h-[18px] w-[18px]" />
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {/* Carpeta vacía / sin resultados */}
        {!fileLoading && !fileError && visibleFiles.length === 0 && (
          <div className="py-12 text-center">
            <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-cafe-500 ${SOFT_SHADOW}`}>
              {searchTerm.trim() ? <Search className="h-6 w-6" /> : <FolderOpen className="h-6 w-6" />}
            </div>
            <h4 className="font-display text-lg font-semibold text-cafe-700">
              {searchTerm.trim() ? "Sin resultados" : "Esta carpeta está vacía"}
            </h4>
            <p className="mt-1 text-sm text-neutral-400">
              {searchTerm.trim()
                ? `No se encontraron archivos con «${searchTerm.trim()}».`
                : "Los archivos aparecerán aquí cuando el administrador los suba."}
            </p>
          </div>
        )}

        {!fileLoading && !fileError && files.length > 0 && (
          <p className="mt-6 text-right text-xs text-neutral-400">{counterText}</p>
        )}
      </section>

      {/* Vista previa: se abre sobre la lista */}
      {selectedFileForPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cafe-800/40 p-3 backdrop-blur-sm sm:p-6"
          onClick={() => setSelectedFileForPreview(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Vista previa de ${selectedFileForPreview.name}`}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between gap-3 border-b border-cafe-100/70 px-4 py-3 sm:px-6 sm:py-4">
              <div className="min-w-0">
                <h3 className="truncate font-display text-lg font-semibold text-cafe-700">{selectedFileForPreview.name}</h3>
                <p className="text-xs text-neutral-400">Vista previa</p>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                <button
                  onClick={() => downloadFile(selectedFileForPreview)}
                  title="Descargar"
                  aria-label={`Descargar ${selectedFileForPreview.name}`}
                  className={`rounded-lg bg-cafe-100/50 p-2 text-cafe-700 transition-colors hover:bg-cafe-100 hover:text-cafe-800 ${FOCUS}`}
                >
                  <Download className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setSelectedFileForPreview(null)}
                  aria-label="Cerrar vista previa"
                  className={`rounded-lg p-2 text-neutral-400 transition-colors hover:bg-cafe-100/50 hover:text-cafe-700 ${FOCUS}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-cafe-100/25 p-3 sm:p-6">
              {selectedFileForPreview.extension === "pdf" ? (
                <iframe
                  src={selectedFileForPreview.previewUrl}
                  className="h-[70vh] w-full rounded-xl bg-white"
                  title={`Vista previa de ${selectedFileForPreview.name}`}
                />
              ) : IMAGE_EXTENSIONS.includes(selectedFileForPreview.extension) ? (
                <img
                  src={selectedFileForPreview.previewUrl}
                  alt={selectedFileForPreview.name}
                  className="mx-auto h-auto max-h-[70vh] max-w-full rounded-xl shadow-lg"
                />
              ) : (
                <div className="py-12 text-center">
                  <Folder className="mx-auto mb-3 h-12 w-12 text-cafe-300" />
                  <p className="text-sm text-neutral-400">Vista previa no disponible para este tipo de archivo.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
